import GoogleAnalytics from './analytics/google';
import CustomService from './analytics/custom';
import {
  EVENT_TRACKING_POSITION,
  EVENT_TRACKING_ACTION,
  EVENT_TRACKING_ACTION_PROGRESS,
  SEND_CONTENT_EVENTS,
  METADATA_TYPE_PODCAST,
  METADATA_TYPE_STREAM
} from './constants';

const defaultOptions = {
  category: false,
  label: false,
  url: '',
  events: {
    start: true,
    complete: true,
    progress: true,
    adskip: true,
    download: true,
    pause: false,
    resume: true,
    mute: false,
    unmute: false,
    seek: false
  }
};

/**
 * Creates an analytics api
 *
 * @param {iono.Player} player  A player api instance
 * @param {Object}      options
 * @return {Object} The analytics api
 */
export default function Analytics (player, logger, computed, options) {
  let analyticsOptions = options.analytics;

  analyticsOptions = Object.assign({}, defaultOptions, analyticsOptions);
  Object.assign(analyticsOptions, defaultOptions.events, analyticsOptions.events);

  const services = {};

  // only enable the custom analytics service when configured for it
  if (typeof analyticsOptions.url === 'string') {
    services.custom = CustomService(player, computed, logger, analyticsOptions);
  }

  // only enable the Google Analytics service when configured for it
  if (options.googleAnalytics.enabled) {
    services.google = GoogleAnalytics(player, computed, logger, analyticsOptions, options.googleAnalytics);
  }

  /**
   * Build a context object from an event
   *
   * @param  {Object} event A player event
   *
   * @return {Object}
   */
  function eventContext (event) {
    const context = event.context;
    context.event = getEventAction(event);
    return context;
  }

  /**
   * Get the analytics event action from a player event
   *
   * @param {Object} event A player event
   * @return {String}
   */
  function getEventAction (event) {
    if (event.details.event === EVENT_TRACKING_POSITION) {
      return EVENT_TRACKING_ACTION_PROGRESS;
    }

    if (event.details.event === EVENT_TRACKING_ACTION) {
      return String(event.details.action);
    }

    return String(event.details.event);
  }

  /**
   * Retrieve the service tags from a playlist item
   *
   * @param {Object} event The event being tracked
   * @return {Object}
   */
  function getServiceTags (serviceName, event, context) {
    return (context.tags || []).reduce(function (tags, tag) {
      if (tag.service === serviceName || tag.service === 'all') {
        tags[tag.name] = computed.parse(tag.value, context);
      }
      return tags;
    }, {});
  }

  /**
   * Send a player event to all analytics services
   *
   * @param {Object} event The event to be tracked
   */
  function send (event) {
    const context = eventContext(event);

    const contentContentTypes = [METADATA_TYPE_PODCAST, METADATA_TYPE_STREAM];

    if (analyticsOptions.events[context.event]) {
      Object.keys(services).forEach(function (serviceName) {
        if (!services[serviceName]) return;

        // skip loading analytics endpoints if built to not send content events
        if (
          // only skip for iono analytics tracking
          serviceName === 'custom' &&
          // only skip if built not to send content events
          SEND_CONTENT_EVENTS === false &&
          // only skip content analytics
          contentContentTypes.includes(context.contentType)
        ) {
          return;
        }

        const service = services[serviceName];
        const serviceTags = getServiceTags(serviceName, event, context);
        const serviceContext = Object.assign({}, serviceTags, context);

        logger.debug(`Analytics: sending analytics to ${serviceName}`);
        service.send(event, serviceContext);
      });
    }
  }

  player.on('tracking', send);

  return {
    services: services,
    options: analyticsOptions,
    send: send
  };
}
